import {
  faUserDoctor,
  faCode,
  faCreditCard,
  faFileInvoiceDollar,
  faClipboardCheck,
  faDollarSign,
  faFilter,
  faUsers,
  faTag,
  faSchool,
  faGraduationCap,
} from '@fortawesome/pro-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';
import { SECTION_ORDER } from '../../constants';

// Re-export SECTION_ORDER for backwards compatibility
export { SECTION_ORDER };

/**
 * Configuration for each searchable criteria type in the slash command menu
 */
export interface SlashMenuTypeConfig {
  type: CriteriaComponentType;
  label: string;
  labelPlural: string;
  colorTheme: string;
  icon: IconDefinition;
  bgColorClass: string;
  textColorClass: string;
  borderColorClass: string;
}

/**
 * Mapping of criteria types to their display configuration
 */
export const SLASH_MENU_TYPE_CONFIGS: Record<
  CriteriaComponentType,
  SlashMenuTypeConfig
> = {
  providers: {
    type: 'providers',
    label: 'Provider',
    labelPlural: 'Providers',
    colorTheme: 'blue',
    icon: faUserDoctor,
    bgColorClass: 'bg-blue-50',
    textColorClass: 'text-blue-700',
    borderColorClass: 'border-blue-200',
  },
  codes: {
    type: 'codes',
    label: 'Code',
    labelPlural: 'Codes',
    colorTheme: 'green',
    icon: faCode,
    bgColorClass: 'bg-green-50',
    textColorClass: 'text-green-700',
    borderColorClass: 'border-green-200',
  },
  payer_type: {
    type: 'payer_type',
    label: 'Payer Type',
    labelPlural: 'Payer Types',
    colorTheme: 'pink',
    icon: faCreditCard,
    bgColorClass: 'bg-pink-50',
    textColorClass: 'text-pink-700',
    borderColorClass: 'border-pink-200',
  },
  payer_name: {
    type: 'payer_name',
    label: 'Payer Name',
    labelPlural: 'Payer Names',
    colorTheme: 'sky',
    icon: faFileInvoiceDollar,
    bgColorClass: 'bg-sky-50',
    textColorClass: 'text-sky-700',
    borderColorClass: 'border-sky-200',
  },
  crm_status: {
    type: 'crm_status',
    label: 'CRM Status',
    labelPlural: 'CRM Statuses',
    colorTheme: 'teal',
    icon: faClipboardCheck,
    bgColorClass: 'bg-teal-50',
    textColorClass: 'text-teal-700',
    borderColorClass: 'border-teal-200',
  },
  open_payments: {
    type: 'open_payments',
    label: 'Open Payment',
    labelPlural: 'Open Payments',
    colorTheme: 'amber',
    icon: faDollarSign,
    bgColorClass: 'bg-amber-50',
    textColorClass: 'text-amber-700',
    borderColorClass: 'border-amber-200',
  },
  open_payments_category: {
    type: 'open_payments_category',
    label: 'Open Payments Category',
    labelPlural: 'Open Payments Categories',
    colorTheme: 'violet',
    icon: faTag,
    bgColorClass: 'bg-violet-50',
    textColorClass: 'text-violet-700',
    borderColorClass: 'border-violet-200',
  },
  filter_set: {
    type: 'filter_set',
    label: 'Filter Set',
    labelPlural: 'Filter Sets',
    colorTheme: 'indigo',
    icon: faFilter,
    bgColorClass: 'bg-indigo-50',
    textColorClass: 'text-indigo-700',
    borderColorClass: 'border-indigo-200',
  },
  educational_institution: {
    type: 'educational_institution',
    label: 'Educational Institution',
    labelPlural: 'Educational Institutions',
    colorTheme: 'amber',
    icon: faSchool,
    bgColorClass: 'bg-amber-50',
    textColorClass: 'text-amber-700',
    borderColorClass: 'border-amber-200',
  },
  provider_cohort: {
    type: 'provider_cohort',
    label: 'Encounter',
    labelPlural: 'Encounters',
    colorTheme: 'orange',
    icon: faUsers,
    bgColorClass: 'bg-orange-50',
    textColorClass: 'text-orange-700',
    borderColorClass: 'border-orange-200',
  },
  // Types that are not searchable (no values, just references)
  education_focus: {
    type: 'education_focus',
    label: 'Education Focus',
    labelPlural: 'Education Focuses',
    colorTheme: 'rose',
    icon: faGraduationCap,
    bgColorClass: 'bg-rose-50',
    textColorClass: 'text-rose-700',
    borderColorClass: 'border-rose-200',
  },
  education_program_type: {
    type: 'education_program_type',
    label: 'Education Program Type',
    labelPlural: 'Education Program Types',
    colorTheme: 'cyan',
    icon: faGraduationCap,
    bgColorClass: 'bg-cyan-50',
    textColorClass: 'text-cyan-700',
    borderColorClass: 'border-cyan-200',
  },
  code_set: {
    type: 'code_set',
    label: 'Code Set',
    labelPlural: 'Code Sets',
    colorTheme: 'pink',
    icon: faCode,
    bgColorClass: 'bg-pink-50',
    textColorClass: 'text-pink-700',
    borderColorClass: 'border-pink-200',
  },
  entity_attribute: {
    type: 'entity_attribute',
    label: 'Site of Care Attribute',
    labelPlural: 'Site of Care Attributes',
    colorTheme: 'rose',
    icon: faFilter,
    bgColorClass: 'bg-rose-50',
    textColorClass: 'text-rose-700',
    borderColorClass: 'border-rose-200',
  },
  entity_type: {
    type: 'entity_type',
    label: 'Entity Type',
    labelPlural: 'Entity Types',
    colorTheme: 'cyan',
    icon: faFilter,
    bgColorClass: 'bg-cyan-50',
    textColorClass: 'text-cyan-700',
    borderColorClass: 'border-cyan-200',
  },
  specialties: {
    type: 'specialties',
    label: 'Specialty',
    labelPlural: 'Specialties',
    colorTheme: 'purple',
    icon: faUserDoctor,
    bgColorClass: 'bg-purple-50',
    textColorClass: 'text-purple-700',
    borderColorClass: 'border-purple-200',
  },
  this_provider: {
    type: 'this_provider',
    label: 'This Provider',
    labelPlural: 'This Provider',
    colorTheme: 'purple',
    icon: faUserDoctor,
    bgColorClass: 'bg-purple-50',
    textColorClass: 'text-purple-700',
    borderColorClass: 'border-purple-200',
  },
  this_site: {
    type: 'this_site',
    label: 'This Site',
    labelPlural: 'This Site',
    colorTheme: 'teal',
    icon: faFilter,
    bgColorClass: 'bg-teal-50',
    textColorClass: 'text-teal-700',
    borderColorClass: 'border-teal-200',
  },
  provider_list: {
    type: 'provider_list',
    label: 'Provider List',
    labelPlural: 'Provider Lists',
    colorTheme: 'lime',
    icon: faFilter,
    bgColorClass: 'bg-lime-50',
    textColorClass: 'text-lime-700',
    borderColorClass: 'border-lime-200',
  },
};

/**
 * Get configuration for a criteria type
 */
export const getTypeConfig = (
  type: CriteriaComponentType
): SlashMenuTypeConfig => {
  return (
    SLASH_MENU_TYPE_CONFIGS[type] || {
      type,
      label: type,
      labelPlural: type,
      colorTheme: 'neutral',
      icon: faFilter,
      bgColorClass: 'bg-neutral-50',
      textColorClass: 'text-neutral-700',
      borderColorClass: 'border-neutral-200',
    }
  );
};
